import { useState, type ReactNode } from "react";
import {
    Box,
    Briefcase,
    Check,
    Gauge,
    GripVertical,
    Loader2,
    Pencil,
    Plus,
    Receipt,
    Sparkles,
    Split,
    Trash2,
    TriangleAlert,
    Undo2,
    Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { formatDollars } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { RichLineText } from "@/components/rich-line-text";
import { PipeChoiceStrip } from "./pipe-choice-strip";
import { OfficeOnlyAssemblyNote } from "@/components/office-only-assembly-note";
import { FeeAudiencePick } from "./fee-audience-pick";
import { DeviceChips, SizeBadge, type DeviceMenuPick } from "@/components/device-menu";
import {
    canSplitShared,
    deviceTitle,
    devicesOnQuote,
    formatQty,
    groupedDropOrder,
    groupLines,
    locationOnFile,
    PIECES,
    sizeBadge,
    type EditorDevice,
    type EditorLine,
    type LineSection,
    type Piece,
} from "./model";
import { LineOriginKey, LineOriginTag, lineOrigin, removesInOneTap } from "./line-origin";

export interface PiecePreview {
    piece: Piece;
    description: string;
    total: number;
}

const PIECE_ICON: Record<Piece, typeof Wrench> = { labor: Wrench, kit: Box, test: Gauge, fee: Receipt };

/** Grid columns shared by the header row and every line (desktop). */
const GRID = "sm:grid sm:grid-cols-[44px_minmax(0,1fr)_52px_86px_92px_66px_60px] sm:items-center sm:gap-x-2 sm:px-3";

/**
 * The grouped Line Items list (Faster Estimates, approved mockup): one sticky
 * header per device with what its repair has (labor / kit / test / city fee —
 * green present, amber missing) and its subtotal; the device column is gone
 * because the header names the device. Lines tick for the floating bar, drag
 * onto another device, and carry their "Reworded · Undo" / "Reword?" pills.
 * Dropping a line ON another line moves it to that spot (10/28): inside its own
 * group that is a reorder; in another group it is tagged there and placed.
 */
export function GroupedLineList({
    lines,
    devices,
    canEdit,
    selected,
    onToggle,
    onTag,
    onMove,
    onSplit,
    onReword,
    onUndo,
    onEdit,
    onDelete,
    editingId,
    renderEditor,
    renderLabor,
    renderTax,
    previews,
    onAddMissing,
    addingPiece,
    onAddLine,
    chips,
    busyLineIds,
}: {
    lines: readonly EditorLine[];
    devices: readonly EditorDevice[];
    canEdit: boolean;
    selected: ReadonlySet<string>;
    onToggle: (id: string) => void;
    onTag: (lineIds: string[], pick: DeviceMenuPick) => void;
    /** Save `order` (every line id); when `pick` is set, tag the line there first. */
    onMove: (lineId: string, order: string[], pick: DeviceMenuPick | null) => void;
    onSplit: (line: EditorLine, assetIds: string[]) => void;
    onReword: (line: EditorLine) => void;
    onUndo: (line: EditorLine) => void;
    onEdit: (line: EditorLine) => void;
    onDelete: (line: EditorLine) => void;
    editingId: string | null;
    renderEditor: (line: EditorLine) => ReactNode;
    renderLabor: (line: EditorLine) => ReactNode;
    renderTax: (line: EditorLine) => ReactNode;
    previews: ReadonlyMap<string, PiecePreview[] | null>;
    onAddMissing: (device: EditorDevice, piece: Piece) => void;
    addingPiece: { deviceId: string; piece: Piece } | null;
    onAddLine: (device: EditorDevice) => void;
    chips: Readonly<Record<string, string>>;
    busyLineIds: ReadonlySet<string>;
}) {
    const sections = groupLines(lines, devices);
    const quoteDevices = devicesOnQuote(lines);
    const [dragId, setDragId] = useState<string | null>(null);
    const [dropKey, setDropKey] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);

    const pickFor = (s: LineSection): DeviceMenuPick =>
        s.kind === "device" && s.device
            ? { kind: "device", device: s.device, work: "repair" }
            : s.kind === "whole"
              ? { kind: "whole" }
              : { kind: "none" };

    // Moving down lands below the target, moving up lands above it.
    const shownIds = sections.flatMap((s) => s.lines.map((l) => l.id));
    const markFor = (id: string): "above" | "below" | null =>
        overId !== id || !dragId ? null : shownIds.indexOf(dragId) < shownIds.indexOf(id) ? "below" : "above";
    const endDrag = () => {
        setDragId(null);
        setDropKey(null);
        setOverId(null);
    };
    const dropOnLine = (targetId: string) => {
        const id = dragId;
        endDrag();
        if (!id) return;
        const plan = groupedDropOrder(lines, devices, id, targetId);
        if (plan.kind === "refused") toast.error(plan.message);
        if (plan.kind === "move") onMove(id, plan.order, plan.to ? pickFor(plan.to) : null);
    };

    return (
        <div className="rounded-[14px] bg-card text-card-foreground shadow-card" data-testid="grouped-line-list">
            <div
                className={cn(
                    "sticky top-0 z-[4] hidden h-[34px] rounded-t-[14px] border-b bg-card text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground",
                    GRID,
                )}
            >
                <span />
                <span>Description</span>
                <span className="text-right">Qty</span>
                <span className="text-right">Price</span>
                <span className="text-right">Total</span>
                <span>Tax</span>
                <span />
            </div>
            {sections.map((s) => (
                <section
                    key={s.key}
                    data-section={s.key}
                    className={cn(dropKey === s.key && "[&>header]:shadow-[inset_0_0_0_2px_var(--color-primary)]")}
                    onDragOver={(e) => {
                        if (!dragId || !canEdit) return;
                        e.preventDefault();
                        if (dropKey !== s.key) setDropKey(s.key);
                    }}
                    onDragLeave={(e) => {
                        if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDropKey(null);
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        const id = dragId;
                        setDragId(null);
                        setDropKey(null);
                        if (!id) return;
                        const line = lines.find((l) => l.id === id);
                        const here =
                            s.kind === "device"
                                ? line?.asset_id === s.key
                                : s.kind === "whole"
                                  ? !line?.asset_id && !!line?.whole_job
                                  : !line?.asset_id && !line?.whole_job;
                        if (!here) onTag([id], pickFor(s));
                    }}
                >
                    {!(s.kind === "none" && s.lines.length === 0) && (
                        <SectionHeader
                            section={s}
                            canEdit={canEdit}
                            preview={s.device ? (previews.get(s.device.id) ?? null) : null}
                            onAddMissing={onAddMissing}
                            addingPiece={addingPiece}
                            onAddLine={onAddLine}
                        />
                    )}
                    {s.kind === "none" && s.lines.length === 0 ? (
                        <div className="flex items-center gap-2 px-3.5 py-3 text-[12.5px] font-semibold text-success">
                            <Check className="h-4 w-4" />
                            Every line has a device
                        </div>
                    ) : (
                        s.lines.map((l) => (
                            <LineRow
                                key={l.id}
                                line={l}
                                canEdit={canEdit}
                                selected={selected.has(l.id)}
                                onToggle={onToggle}
                                dragging={dragId === l.id}
                                onDragStart={() => setDragId(l.id)}
                                onDragEnd={endDrag}
                                dropMark={markFor(l.id)}
                                onDragOverLine={() => {
                                    if (!dragId || !canEdit || dragId === l.id) return false;
                                    if (overId !== l.id) setOverId(l.id);
                                    return true;
                                }}
                                onDropOnLine={() => dropOnLine(l.id)}
                                editing={editingId === l.id}
                                renderEditor={renderEditor}
                                renderLabor={renderLabor}
                                renderTax={renderTax}
                                chip={chips[l.id] ?? null}
                                busy={busyLineIds.has(l.id)}
                                splitTo={canEdit && canSplitShared(l, quoteDevices) ? quoteDevices : null}
                                onSplit={onSplit}
                                onReword={onReword}
                                onUndo={onUndo}
                                onEdit={onEdit}
                                onDelete={onDelete}
                            />
                        ))
                    )}
                </section>
            ))}
            {lines.some((l) => lineOrigin(l) !== null) && <LineOriginKey />}
        </div>
    );
}

function SectionHeader({
    section: s,
    canEdit,
    preview,
    onAddMissing,
    addingPiece,
    onAddLine,
}: {
    section: LineSection;
    canEdit: boolean;
    preview: PiecePreview[] | null;
    onAddMissing: (device: EditorDevice, piece: Piece) => void;
    addingPiece: { deviceId: string; piece: Piece } | null;
    onAddLine: (device: EditorDevice) => void;
}) {
    const base =
        "sticky top-0 z-[3] flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border-y px-3.5 py-2 backdrop-blur-md sm:top-[34px]";
    if (s.kind === "none") {
        return (
            <header className={cn(base, "bg-warning/10")}>
                <SizeBadge tone="warn">
                    <TriangleAlert className="h-3.5 w-3.5" />
                </SizeBadge>
                <span className="flex min-w-0 flex-col leading-tight">
                    <b className="text-[13.5px] font-bold">No device yet</b>
                    <small className="text-[11px] text-muted-foreground">
                        {s.lines.length === 0
                            ? "Nothing waiting"
                            : `${s.lines.length} line${s.lines.length === 1 ? "" : "s"} · tick and tag, or drag up`}
                    </small>
                </span>
            </header>
        );
    }
    if (s.kind === "whole") {
        return (
            <header className={cn(base, "bg-muted/85")}>
                <SizeBadge>
                    <Briefcase className="h-3.5 w-3.5" />
                </SizeBadge>
                <span className="flex min-w-0 flex-col leading-tight">
                    <b className="text-[13.5px] font-bold">Whole job</b>
                    <small className="text-[11px] text-muted-foreground">Trip charge and other visit costs</small>
                </span>
                <span className="ml-auto min-w-[78px] text-right text-[13px] font-bold tabular-nums">
                    {formatDollars(s.subtotal)}
                </span>
            </header>
        );
    }
    const d = s.device!;
    const where = locationOnFile(d.location_description);
    const previewFor = (p: Piece) => preview?.filter((x) => x.piece === p) ?? [];
    return (
        <header className={cn(base, "bg-muted/85")} data-testid="device-header">
            <SizeBadge>{sizeBadge(d.size_label) ?? "—"}</SizeBadge>
            <span className="flex min-w-0 flex-col leading-tight">
                <b className="text-[13.5px] font-bold">{deviceTitle(d)}</b>
                <small className="text-[11px] text-muted-foreground">{where ?? "location not on file"}</small>
            </span>
            <DeviceChips device={d} />
            <span className="ml-auto inline-flex flex-wrap items-center gap-1">
                {canEdit &&
                    PIECES.filter(({ piece }) => !s.has[piece] && previewFor(piece).length > 0).map(
                        ({ piece, label }) => {
                            const amount = previewFor(piece).reduce((t, x) => t + x.total, 0);
                            const busy = addingPiece?.deviceId === d.id && addingPiece.piece === piece;
                            return (
                                <button
                                    key={piece}
                                    type="button"
                                    className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-1 text-[11.5px] font-bold text-warning hover:brightness-95 disabled:opacity-60"
                                    disabled={!!addingPiece}
                                    title={previewFor(piece)
                                        .map((x) => x.description)
                                        .join(" · ")}
                                    onClick={() => onAddMissing(d, piece)}
                                >
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                                    {label} · {formatDollars(amount)}
                                </button>
                            );
                        },
                    )}
                {PIECES.map(({ piece, label }) => {
                    const Icon = PIECE_ICON[piece];
                    const has = s.has[piece];
                    // A fee the price list wouldn't add (no fee for this city) isn't a gap.
                    const notNeeded = !has && piece === "fee" && preview !== null && previewFor("fee").length === 0;
                    return (
                        <span
                            key={piece}
                            role="img"
                            aria-label={`${label}: ${has ? "on this repair" : notNeeded ? "none for this location" : "missing"}`}
                            title={`${label}: ${has ? "on this repair" : notNeeded ? "none for this location" : "missing"}`}
                            className={cn(
                                "inline-flex h-6 w-6 items-center justify-center rounded-[7px]",
                                has
                                    ? "bg-success/15 text-success"
                                    : notNeeded
                                      ? "border border-dashed border-border text-muted-foreground/60"
                                      : "border-[1.5px] border-dashed border-warning text-warning",
                            )}
                        >
                            <Icon className="h-3.5 w-3.5" />
                        </span>
                    );
                })}
            </span>
            <span className="min-w-[78px] text-right text-[13px] font-bold tabular-nums">
                {formatDollars(s.subtotal)}
            </span>
            {canEdit && (
                <button
                    type="button"
                    title="Add a line to this device"
                    aria-label="Add a line to this device"
                    className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-lg border bg-card text-primary hover:bg-primary/10"
                    onClick={() => onAddLine(d)}
                >
                    <Plus className="h-3.5 w-3.5" />
                </button>
            )}
        </header>
    );
}

function LineRow({
    line: l,
    canEdit,
    selected,
    onToggle,
    dragging,
    onDragStart,
    onDragEnd,
    dropMark,
    onDragOverLine,
    onDropOnLine,
    editing,
    renderEditor,
    renderLabor,
    renderTax,
    chip,
    busy,
    splitTo,
    onSplit,
    onReword,
    onUndo,
    onEdit,
    onDelete,
}: {
    line: EditorLine;
    canEdit: boolean;
    selected: boolean;
    onToggle: (id: string) => void;
    dragging: boolean;
    onDragStart: () => void;
    onDragEnd: () => void;
    /** A line is being dragged over this one: show where it will land. */
    dropMark: "above" | "below" | null;
    /** True when this line takes the drop. */
    onDragOverLine: () => boolean;
    onDropOnLine: () => void;
    editing: boolean;
    renderEditor: (line: EditorLine) => ReactNode;
    renderLabor: (line: EditorLine) => ReactNode;
    renderTax: (line: EditorLine) => ReactNode;
    chip: string | null;
    busy: boolean;
    splitTo: string[] | null;
    onSplit: (line: EditorLine, assetIds: string[]) => void;
    onReword: (line: EditorLine) => void;
    onUndo: (line: EditorLine) => void;
    onEdit: (line: EditorLine) => void;
    onDelete: (line: EditorLine) => void;
}) {
    if (editing) {
        return <div className="border-b bg-primary/5 px-3 py-3">{renderEditor(l)}</div>;
    }
    const reworded = l.reworded_from != null;
    const pillText = chip ?? (reworded ? "Reworded" : null);
    const labor = renderLabor(l);
    const offer = canEdit && !reworded && l.reword_offer ? l.reword_offer : null;
    const origin = lineOrigin(l);
    const meta = labor || pillText || offer || splitTo || origin;
    return (
        <div
            data-line-id={l.id}
            draggable={canEdit}
            onDragStart={(e) => {
                try {
                    e.dataTransfer.setData("text/plain", l.id);
                    e.dataTransfer.effectAllowed = "move";
                } catch {
                    /* some browsers refuse custom drag data */
                }
                onDragStart();
            }}
            onDragEnd={onDragEnd}
            onDragOver={(e) => {
                if (onDragOverLine()) e.preventDefault();
            }}
            onDrop={(e) => {
                // The line takes the drop; the group around it must not tag it too.
                e.preventDefault();
                e.stopPropagation();
                onDropOnLine();
            }}
            className={cn(
                "group relative flex flex-wrap items-start gap-x-2 gap-y-1.5 border-b px-3 py-2 text-[13px] transition-colors hover:bg-muted/40 sm:min-h-[46px] sm:py-[7px]",
                GRID,
                selected && "bg-primary/10 hover:bg-primary/10",
                dragging && "opacity-40",
                dropMark === "above" && "shadow-[inset_0_2px_0_0_var(--color-primary)]",
                dropMark === "below" && "shadow-[inset_0_-2px_0_0_var(--color-primary)]",
            )}
        >
            <span className="flex items-center gap-1.5 pt-0.5 text-muted-foreground sm:pt-0">
                {canEdit && (
                    <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-primary"
                        aria-label={`Select line: ${l.description}`}
                        checked={selected}
                        onChange={() => onToggle(l.id)}
                        data-testid="line-select"
                    />
                )}
                {canEdit && (
                    <span
                        className="hidden cursor-grab opacity-0 transition-opacity group-hover:opacity-100 sm:inline-flex"
                        aria-hidden
                    >
                        <GripVertical className="h-3.5 w-3.5" />
                    </span>
                )}
            </span>
            <span className="flex min-w-0 flex-1 basis-[calc(100%-40px)] flex-col gap-1 sm:basis-auto">
                <RichLineText html={l.description_html} text={l.description} className="leading-snug" />
                <PipeChoiceStrip line={l} canEdit={canEdit} />
                <OfficeOnlyAssemblyNote note={l.office_assembly} />
                <FeeAudiencePick line={l} doc="estimate" docId={l.estimate_id} canEdit={canEdit} />
                {meta && (
                    <span className="flex flex-wrap items-center gap-1.5">
                        <LineOriginTag line={l} />
                        {labor}
                        {pillText && (
                            <span
                                className={cn(
                                    "inline-flex items-center gap-1 rounded-full bg-blue-500/10 py-0.5 pl-2 text-[11px] font-semibold text-blue-700 dark:text-blue-300",
                                    reworded ? "pr-1" : "pr-2",
                                )}
                                title={reworded ? `Was: ${l.reworded_from}` : undefined}
                            >
                                <Sparkles className="h-3 w-3" />
                                {pillText}
                                {reworded && canEdit && (
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-0.5 rounded-full bg-card px-1.5 py-0.5 text-[10.5px] font-bold hover:bg-blue-600 hover:text-white"
                                        aria-label="Undo wording"
                                        disabled={busy}
                                        onClick={() => onUndo(l)}
                                    >
                                        <Undo2 className="h-3 w-3" />
                                        Undo
                                    </button>
                                )}
                            </span>
                        )}
                        {offer && (
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold hover:bg-primary/10 hover:text-primary"
                                title={`Reword to: ${offer}`}
                                disabled={busy}
                                onClick={() => onReword(l)}
                            >
                                <Sparkles className="h-3 w-3" />
                                Reword?
                            </button>
                        )}
                        {splitTo && (
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold hover:bg-primary/10 hover:text-primary"
                                disabled={busy}
                                onClick={() => onSplit(l, splitTo)}
                            >
                                <Split className="h-3 w-3" />
                                Split 1 per device
                            </button>
                        )}
                    </span>
                )}
            </span>
            {/* Numbers: their own columns on desktop, one tidy line on a phone. */}
            <span className="ml-[30px] flex w-full items-center gap-3 text-[12.5px] tabular-nums text-muted-foreground sm:contents sm:text-[13px] sm:text-foreground">
                <span className="whitespace-nowrap sm:text-right" data-testid="line-qty">
                    <span className="sm:hidden">Qty </span>
                    {formatQty(l.qty)}
                </span>
                <span className="whitespace-nowrap sm:text-right">
                    <span className="sm:hidden">× </span>
                    {formatDollars(Number(l.unit_price))}
                </span>
                <span className="ml-auto font-semibold text-foreground sm:ml-0 sm:text-right">
                    {formatDollars(Number(l.line_total))}
                </span>
                <span className="sm:justify-self-start">{renderTax(l)}</span>
                <span className="inline-flex justify-end gap-0.5 opacity-100 transition-opacity sm:opacity-45 sm:group-hover:opacity-100">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" aria-label="Saving" />}
                    {canEdit && (
                        <>
                            <button
                                type="button"
                                title="Edit"
                                aria-label="Edit line"
                                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-primary"
                                onClick={() => onEdit(l)}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                                type="button"
                                title={removesInOneTap(l) ? "Remove (Undo puts it back)" : "Remove"}
                                aria-label="Remove line"
                                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-destructive"
                                onClick={() => onDelete(l)}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </>
                    )}
                </span>
            </span>
        </div>
    );
}
